/**
 * How often this product has been shown, and what people were searching for.
 *
 * The panel exists to answer a question the request log cannot: the log is
 * organised by request, so "has anyone ever been served this product, and under
 * which code?" means reading thousands of rows. Here it is one query, and the
 * useful half is not the total — it is the list of searches, which shows whether a
 * product is only ever reached by the one code we invented for it, or by the
 * codes customers actually type.
 */
import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../lib/api';
import { Chip, Empty, Loading, Seg, SourceChip, Table } from '../ui';

interface Hits {
  total: number;
  totalAllTime: number;
  firstAt: string | null;
  lastAt: string | null;
  searches: { source: string; op: string; mode: string; query: string; count: number; lastAt: string }[];
  daily: { day: string; count: number }[];
  recent: { source: string; op: string; mode: string; query: string; at: string; traceId: string }[];
}

const RANGES: { value: string; label: string; days: number | null }[] = [
  { value: '7', label: '7 days', days: 7 },
  { value: '30', label: '30 days', days: 30 },
  { value: '90', label: '90 days', days: 90 },
  { value: 'all', label: 'All time', days: null },
];

/** What the caller was asking, in words a person reads without a legend. */
const MODE_LABEL: Record<string, string> = {
  article: 'code search',
  oe: 'OE search',
  cross: 'cross search',
  ean: 'EAN search',
  text: 'text search',
  directory: 'brand list',
  other: 'other',
};

function Sparkline({ days }: { days: { day: string; count: number }[] }) {
  if (days.length === 0) return null;
  const peak = Math.max(...days.map((d) => d.count), 1);
  return (
    <div className="flex items-end gap-[2px]" style={{ height: 34 }}>
      {days.map((d) => (
        <div
          key={d.day}
          className="flex-1 rounded-t bg-accent"
          style={{ height: `${Math.max((d.count / peak) * 100, 4)}%`, minWidth: 3 }}
          title={`${d.day} · ${d.count}`}
        />
      ))}
    </div>
  );
}

export default function ProductHits({ productId }: { productId: number }) {
  const { request } = useApi();
  const [range, setRange] = useState('30');
  const [data, setData] = useState<Hits | null>(null);

  const load = useCallback(async () => {
    const days = RANGES.find((r) => r.value === range)?.days ?? null;
    const from = days === null ? '' : `?from=${new Date(Date.now() - days * 86_400_000).toISOString()}`;
    const r = await request<Hits>('GET', `/v1/products/${productId}/hits${from}`);
    if (r.ok) setData(r.data);
  }, [productId, range, request]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!data) return <Loading />;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <span className="num text-[22px] font-semibold">{data.total.toLocaleString()}</span>
          <span className="ml-1.5 text-[12px] text-muted">times shown</span>
          {data.totalAllTime !== data.total && (
            <span className="ml-1.5 text-[11.5px] text-faint">({data.totalAllTime.toLocaleString()} all time)</span>
          )}
        </div>
        <Seg options={RANGES.map((r) => ({ value: r.value, label: r.label }))} value={range} onChange={setRange} />
        <div className="flex-1" />
        {data.lastAt && (
          <span className="text-[11.5px] text-faint">last {new Date(data.lastAt).toLocaleString()}</span>
        )}
      </div>

      {data.total === 0 ? (
        <Empty>
          Never returned in this window. A product nobody is served is either not being searched for, or not being
          matched — the searches below would show which.
        </Empty>
      ) : (
        <>
          <Sparkline days={data.daily} />

          <Table cols={['Source', 'Asking', 'Code searched', 'Times', 'Last']}>
            {data.searches.map((s) => (
              <tr key={`${s.source}/${s.op}/${s.mode}/${s.query}`}>
                <td>
                  <SourceChip source={s.source} />
                </td>
                <td>
                  <Chip>{MODE_LABEL[s.mode] ?? s.mode}</Chip>
                </td>
                <td className="mono">{s.query || <span className="text-faint">—</span>}</td>
                <td className="num">{s.count.toLocaleString()}</td>
                <td className="mono text-faint">{new Date(s.lastAt).toLocaleString()}</td>
              </tr>
            ))}
          </Table>
        </>
      )}
    </div>
  );
}
